/**
 * Live Places API Service
 * Integrates with your backend APIs for Places data
 */

// Import test utilities for debugging
import { testBackendConnection } from '../utils/testBackendConnection.js';
import { testAuthentication, quickAuthTest } from '../utils/testAuthentication.js';
import { corsApiCall, testCorsConfiguration } from '../utils/corsProxy.js';

import logger from '../utils/logger';
class LivePlacesAPIService {
  constructor() {
    this.baseURL = process.env.NODE_ENV === 'development'
      ? 'http://localhost:5000/api/v1/customer/profile'
      : 'https://api.visibeen.com/api/v1/customer/profile';
    this.apiKey = process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  }

  /**
   * Text search for places using your live API
   * @param {string} query - Search query
   * @param {number} lat - Latitude (optional)
   * @param {number} lng - Longitude (optional) 
   * @param {number} radius - Search radius in meters (optional, default: 5000)
   * @returns {Promise<Array>} Array of place results
   */
  async textSearch(query, lat = null, lng = null, radius = 5000) {
    try {
      // Clean and simplify the query for better API compatibility
      let cleanQuery = this.cleanSearchQuery(query);

      const params = {
        query: cleanQuery
      };

      if (lat && lng) {
        params.lat = lat.toString();
        params.lng = lng.toString();
        params.radius = radius.toString();
      }

      logger.log('🔍 [LiveAPI] Making text search request');
      logger.log('🔍 [LiveAPI] Original query:', query);
      logger.log('🔍 [LiveAPI] Cleaned query:', cleanQuery);
      logger.log('🔍 [LiveAPI] Parameters:', params);

      // Use CORS-aware API call
      const data = await corsApiCall('/textsearch', params);

      // If API call failed, return empty array instead of throwing
      if (!data) {
        logger.warn('🔍 [LiveAPI] Text search returned no data');
        return [];
      }

      logger.log('🔍 [LiveAPI] Text search response:', data);
      return this.transformTextSearchResults(data.results || []);
    } catch (error) {
      logger.error('🔍 [LiveAPI] Text search error:', error);
      // Return empty array instead of throwing to prevent console errors
      return [];
    }
  }

  /**
   * Clean search query for better API compatibility
   * @param {string} query - Original search query
   * @returns {string} Cleaned query
   */
  cleanSearchQuery(query) {
    if (!query) return '';

    // If query looks like a business name with complex formatting, extract key terms
    if (query.includes('|') || query.includes(' - ') || query.includes(' | ')) {
      // Extract business type keywords
      const businessKeywords = this.extractBusinessKeywords(query);
      if (businessKeywords.length > 0) {
        return businessKeywords.join(' ');
      }
    }

    // Remove location coordinates if present
    const cleanedQuery = query.replace(/near \d+\.\d+,\d+\.\d+/g, '').trim();

    // If still too complex, extract first meaningful part
    if (cleanedQuery.length > 50) {
      const parts = cleanedQuery.split(/[-|]/);
      return parts[0].trim();
    }

    return cleanedQuery;
  }

  /**
   * Extract business keywords from complex business names
   * @param {string} businessName - Complex business name
   * @returns {Array} Array of business keywords
   */
  extractBusinessKeywords(businessName) {
    const keywords = [];
    const name = businessName.toLowerCase();

    // Common business type keywords
    const businessTypes = [
      'electronics', 'electronic', 'electro',
      'restaurant', 'cafe', 'hotel', 'hospital',
      'salon', 'spa', 'gym', 'store', 'shop',
      'dealer', 'service', 'repair', 'clinic',
      'pharmacy', 'medical', 'dental', 'lawyer',
      'real estate', 'automotive', 'garage'
    ];

    // Extract matching business types
    businessTypes.forEach(type => {
      if (name.includes(type)) {
        keywords.push(type);
      }
    });

    // If no business types found, use first word of business name
    if (keywords.length === 0) {
      const firstWord = businessName.split(/[\s-|]/)[0];
      if (firstWord && firstWord.length > 2) {
        keywords.push(firstWord);
      }
    }

    return keywords;
  }

  /**
   * Get detailed place information using your live API
   * @param {string} placeId - Google Place ID
   * @returns {Promise<Object>} Place details
   */
  async getPlaceDetails(placeId) {
    try {
      const params = {
        placeId: placeId
      };

      logger.log('🔍 [LiveAPI] Making place details request');
      logger.log('🔍 [LiveAPI] Place ID:', placeId);

      // Use CORS-aware API call
      const data = await corsApiCall('/placesDetails', params);

      // If API call failed, return null instead of throwing
      if (!data) {
        logger.warn('🔍 [LiveAPI] Place details returned no data');
        return null;
      }

      logger.log('🔍 [LiveAPI] Place details response:', data);
      return this.transformPlaceDetails(data.result);
    } catch (error) {
      logger.error('🔍 [LiveAPI] Place details error:', error);
      // Return null instead of throwing to prevent console errors
      return null;
    }
  }

  /**
   * Get places nearby a location using your live API
   * @param {string} location - Location in "lat,lng" format
   * @param {string} keyword - Search keyword
   * @param {number} radius - Search radius in meters (optional, default: 1000)
   * @returns {Promise<Array>} Array of nearby places
   */
  async getPlacesNearby(location, keyword, radius = 1000) {
    try {
      const params = {
        location: location,
        keyword: keyword,
        radius: radius.toString()
      };

      logger.log('🔍 [LiveAPI] Making nearby places request');
      logger.log('🔍 [LiveAPI] Parameters:', params);

      // Use CORS-aware API call
      const data = await corsApiCall('/getPlacesNearBy', params);

      // If API call failed, return empty array instead of throwing
      if (!data) {
        logger.warn('🔍 [LiveAPI] Nearby places returned no data');
        return [];
      }

      logger.log('🔍 [LiveAPI] Nearby places response:', data);
      return this.transformNearbyResults(data.results || []);
    } catch (error) {
      logger.error('🔍 [LiveAPI] Nearby places error:', error);
      // Return empty array instead of throwing to prevent console errors
      return [];
    }
  }

  /**
   * Transform text search results to standard format
   * @param {Array} results - Raw API results
   * @returns {Array} Transformed results
   */
  transformTextSearchResults(results) {
    return results.map(place => ({
      placeId: place.placeId,
      name: place.name || 'Unknown Business',
      address: place.address || 'Address not available',
      rating: place.rating || 0,
      userRatingCount: place.userRatingCount || 0,
      reviewCount: place.userRatingCount || 0, // Alias for GMB scoring
      types: place.types || [],
      categories: place.types || [], // Alias for GMB scoring
      photoReference: place.photoReference,
      photoCount: place.photoCount || 0,
      businessStatus: place.businessStatus || 'OPERATIONAL',
      websiteUri: place.websiteUri,
      website: place.websiteUri, // Alias for GMB scoring
      nationalPhoneNumber: place.nationalPhoneNumber,
      internationalPhoneNumber: place.internationalPhoneNumber,
      phone: place.nationalPhoneNumber || place.internationalPhoneNumber, // Alias for GMB scoring
      primaryType: place.primaryType,
      latitude: place.latitude,
      longitude: place.longitude,
      openingHours: place.openingHours || [],
      priceLevel: place.priceLevel,
      description: place.description,
      // GMB scoring fields with defaults
      socialLinks: [],
      appointmentLinks: [],
      serviceArea: undefined,
      hasQA: false,
      reviewResponseRate: undefined,
      newReviewsLast30Days: undefined
    }));
  }

  /**
   * Transform place details to standard format
   * @param {Object} place - Raw place details
   * @returns {Object} Transformed place details
   */
  transformPlaceDetails(place) {
    if (!place) return null;

    return {
      placeId: place.placeId,
      name: place.name || 'Unknown Business',
      address: place.address || 'Address not available',
      rating: place.rating || 0,
      userRatingCount: place.userRatingCount || 0,
      reviewCount: place.userRatingCount || 0, // Alias for GMB scoring
      types: place.types || [],
      categories: place.types || [], // Alias for GMB scoring
      photoReference: place.photoReference,
      photoCount: place.photoCount || 0,
      businessStatus: place.businessStatus || 'OPERATIONAL',
      websiteUri: place.websiteUri,
      website: place.websiteUri, // Alias for GMB scoring
      nationalPhoneNumber: place.nationalPhoneNumber,
      internationalPhoneNumber: place.internationalPhoneNumber,
      phone: place.nationalPhoneNumber || place.internationalPhoneNumber, // Alias for GMB scoring
      primaryType: place.primaryType,
      latitude: place.latitude,
      longitude: place.longitude,
      openingHours: place.openingHours || [],
      priceLevel: place.priceLevel,
      description: place.description,
      // GMB scoring fields with defaults
      socialLinks: [],
      appointmentLinks: [],
      serviceArea: undefined,
      hasQA: false,
      reviewResponseRate: undefined,
      newReviewsLast30Days: undefined
    };
  }

  /**
   * Transform nearby places results to standard format
   * @param {Array} results - Raw nearby places results
   * @returns {Array} Transformed results
   */
  transformNearbyResults(results) {
    return results.map(place => ({
      placeId: place.place_id,
      name: place.name || 'Unknown Business',
      address: place.formatted_address || place.vicinity || 'Address not available',
      rating: place.rating || 0,
      userRatingCount: place.user_ratings_total || 0,
      reviewCount: place.user_ratings_total || 0, // Alias for GMB scoring
      types: place.types || [],
      categories: place.types || [], // Alias for GMB scoring
      photoReference: place.photos?.[0]?.photo_reference,
      photoCount: place.photos?.length || 0,
      businessStatus: place.business_status || 'OPERATIONAL',
      websiteUri: undefined, // Not available in nearby search
      website: undefined, // Not available in nearby search
      nationalPhoneNumber: undefined, // Not available in nearby search
      internationalPhoneNumber: undefined, // Not available in nearby search
      phone: undefined, // Not available in nearby search
      primaryType: place.types?.[0],
      latitude: place.geometry?.location?.lat,
      longitude: place.geometry?.location?.lng,
      openingHours: [],
      priceLevel: undefined,
      description: undefined,
      // GMB scoring fields with defaults
      socialLinks: [],
      appointmentLinks: [],
      serviceArea: undefined,
      hasQA: false,
      reviewResponseRate: undefined,
      newReviewsLast30Days: undefined
    }));
  }

  /**
   * Enhanced search combining multiple API calls for comprehensive results
   * @param {Object} params - Search parameters
   * @param {number} params.latitude - Center latitude
   * @param {number} params.longitude - Center longitude
   * @param {string} params.keyword - Search keyword
   * @param {number} params.radius - Search radius in meters
   * @param {string} params.excludePlaceId - Place ID to exclude (optional)
   * @returns {Promise<Array>} Combined search results
   */
  async enhancedSearch({ latitude, longitude, keyword, radius = 5000, excludePlaceId = null }) {
    try {
      logger.log('🔍 [LiveAPI] Starting enhanced search:', { latitude, longitude, keyword, radius });

      const results = new Map(); // Use Map to deduplicate by placeId
      let hasAnyResults = false;

      // 1. Text search with location bias - try multiple query variations
      const queryVariations = this.generateQueryVariations(keyword);

      for (const query of queryVariations) {
        try {
          logger.log('🔍 [LiveAPI] Trying query variation:', query);
          const textResults = await this.textSearch(query, latitude, longitude, radius);

          if (textResults.length > 0) {
            hasAnyResults = true;
            textResults.forEach(place => {
              if (place.placeId && place.placeId !== excludePlaceId) {
                results.set(place.placeId, place);
              }
            });
            logger.log('🔍 [LiveAPI] Text search found:', textResults.length, 'places for query:', query);

            // If we got good results, don't try more variations
            if (textResults.length >= 5) break;
          }
        } catch (error) {
          logger.warn('🔍 [LiveAPI] Text search failed for query:', query, error.message);
          continue; // Try next variation
        }
      }

      // 2. Nearby search for additional results (only if text search had issues)
      if (!hasAnyResults || results.size < 5) {
        try {
          const location = `${latitude},${longitude}`;
          const nearbyResults = await this.getPlacesNearby(location, keyword, radius);
          nearbyResults.forEach(place => {
            if (place.placeId && place.placeId !== excludePlaceId && !results.has(place.placeId)) {
              results.set(place.placeId, place);
            }
          });
          logger.log('🔍 [LiveAPI] Nearby search found:', nearbyResults.length, 'additional places');
          hasAnyResults = hasAnyResults || nearbyResults.length > 0;
        } catch (error) {
          logger.warn('🔍 [LiveAPI] Nearby search failed:', error.message);
        }
      }

      const combinedResults = Array.from(results.values());

      // If no results from live API, return empty array (don't throw error)
      if (combinedResults.length === 0) {
        logger.warn('🔍 [LiveAPI] No results found from any API endpoint');
        return [];
      }

      // Calculate distances and sort by relevance
      combinedResults.forEach(place => {
        if (place.latitude && place.longitude) {
          place.distance = this.calculateDistance(
            latitude, longitude,
            place.latitude, place.longitude
          );
        } else {
          place.distance = 999; // Unknown distance
        }
      });

      // Sort by rating * log(reviews) for relevance
      combinedResults.sort((a, b) => {
        const scoreA = (a.rating || 0) * Math.log10((a.userRatingCount || 0) + 1);
        const scoreB = (b.rating || 0) * Math.log10((b.userRatingCount || 0) + 1);
        return scoreB - scoreA;
      });

      logger.log('🔍 [LiveAPI] Enhanced search completed:', combinedResults.length, 'total unique places');
      return combinedResults.slice(0, 50); // Limit to top 50 results

    } catch (error) {
      logger.error('🔍 [LiveAPI] Enhanced search error:', error);
      // Return empty array instead of throwing to allow graceful fallback
      return [];
    }
  }

  /**
   * Generate query variations for better search results
   * @param {string} originalQuery - Original search query
   * @returns {Array} Array of query variations
   */
  generateQueryVariations(originalQuery) {
    const variations = [];
    const cleanQuery = this.cleanSearchQuery(originalQuery);

    // Add the cleaned query first
    variations.push(cleanQuery);

    // If original was different, add it too (but cleaned)
    if (cleanQuery !== originalQuery) {
      const simpleClean = originalQuery.replace(/[|]/g, '').trim();
      if (simpleClean !== cleanQuery) {
        variations.push(simpleClean);
      }
    }

    // Extract business keywords and create variations
    const keywords = this.extractBusinessKeywords(originalQuery);
    if (keywords.length > 0) {
      // Single keyword searches
      keywords.forEach(keyword => {
        if (!variations.includes(keyword)) {
          variations.push(keyword);
        }
      });

      // Combined keyword searches
      if (keywords.length > 1) {
        const combined = keywords.slice(0, 2).join(' ');
        if (!variations.includes(combined)) {
          variations.push(combined);
        }
      }
    }

    // Limit to 3 variations to avoid too many API calls
    return variations.slice(0, 3);
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   * @param {number} lat1 - First latitude
   * @param {number} lon1 - First longitude
   * @param {number} lat2 - Second latitude
   * @param {number} lon2 - Second longitude
   * @returns {number} Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;

    const R = 6371; // Radius of the Earth in kilometers
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return Math.round(distance * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Convert degrees to radians
   * @param {number} deg - Degrees
   * @returns {number} Radians
   */
  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  /**
   * Test the API endpoints
   * @param {string} query - Test query
   * @param {number} lat - Test latitude
   * @param {number} lng - Test longitude
   * @returns {Promise<Object>} Test results
   */
  async testAPI(query = 'restaurant', lat = 30.7333, lng = 76.7794) {
    logger.log('🧪 [LiveAPI] Starting API test...');

    const results = {
      textSearch: null,
      nearbySearch: null,
      placeDetails: null,
      enhancedSearch: null
    };

    try {
      // Test text search
      logger.log('🧪 [LiveAPI] Testing text search...');
      results.textSearch = await this.textSearch(query, lat, lng);
      logger.log('🧪 [LiveAPI] Text search results:', results.textSearch.length);

      // Test nearby search
      logger.log('🧪 [LiveAPI] Testing nearby search...');
      results.nearbySearch = await this.getPlacesNearby(`${lat},${lng}`, query);
      logger.log('🧪 [LiveAPI] Nearby search results:', results.nearbySearch.length);

      // Test place details (if we have results)
      if (results.textSearch.length > 0) {
        const firstPlace = results.textSearch[0];
        logger.log('🧪 [LiveAPI] Testing place details for:', firstPlace.name);
        results.placeDetails = await this.getPlaceDetails(firstPlace.placeId);
        logger.log('🧪 [LiveAPI] Place details result:', results.placeDetails?.name);
      }

      // Test enhanced search
      logger.log('🧪 [LiveAPI] Testing enhanced search...');
      results.enhancedSearch = await this.enhancedSearch({
        latitude: lat,
        longitude: lng,
        keyword: query,
        radius: 5000
      });
      logger.log('🧪 [LiveAPI] Enhanced search results:', results.enhancedSearch.length);

      logger.log('🧪 [LiveAPI] All tests completed successfully!');
      return results;

    } catch (error) {
      logger.error('🧪 [LiveAPI] Test failed:', error);
      return { error: error.message, results };
    }
  }
}

export default new LivePlacesAPIService();
